<template>
  <div>
    <dx-grid
      name="teacher-relations"
      :beforeSetupGrid="beforeSetupGrid"
      :url="url"
      :columns="columns"
      title="Teacher Relations"
      ref="grid"
      :actions="actions"
      :selectionEnabled="true"
    ></dx-grid>

    <v-dialog
      persistent
      v-model="newRelationDialog"
      :max-width="relationMode === 'staff' ? 1100 : 400"
    >
      <v-card class="pa-3">
        <div class="headline mb-3">New Relation</div>
        <div class="relation-mode mb-3">
          <div
            class="relation-mode__option"
            :class="{ active: relationMode === 'single' }"
            @click="relationMode = 'single'"
          >
            <v-icon small>mdi-account</v-icon>
            Single teacher
          </div>
          <div
            class="relation-mode__option"
            :class="{ active: relationMode === 'staff' }"
            @click="relationMode = 'staff'"
          >
            <v-icon small>mdi-account-multiple</v-icon>
            From staff list
          </div>
        </div>

        <template v-if="relationMode === 'staff'">
          <v-layout row wrap class="mx-0">
            <v-flex xs12 sm4 class="pr-2">
              <v-autocomplete
                hide-details
                label="Account"
                placeholder="Start typing..."
                hide-no-data
                v-model="newRelation.accountID"
                :items="accounts"
                :search-input.sync="loadAccounts"
                @change="onAccountSelected"
              ></v-autocomplete>
              <div
                v-if="newRelation.account.accountID"
                class="caption grey--text text--darken-1 mt-1"
              >
                {{ newRelation.account.hname }}
              </div>
            </v-flex>
            <v-flex xs6 sm4 class="px-2">
              <v-select
                label="Staff from term"
                :items="terms"
                v-model="staffRegID"
                hide-details
                @change="loadTermStaff"
              ></v-select>
            </v-flex>
            <v-flex xs6 sm4 class="pl-2">
              <v-select
                label="Save to term"
                :items="terms"
                v-model="staffTargetRegID"
                hide-details
                @change="loadTermStaff"
              ></v-select>
            </v-flex>
          </v-layout>

          <template v-if="!staffResult && !staffConfirming">
            <v-layout row wrap class="mx-0 mt-2">
              <v-flex xs12 sm8 class="pr-2">
                <v-text-field
                  v-model="staffComment"
                  label="Comment"
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm4 class="pl-2">
                <v-select
                  v-model="staffSource"
                  label="Source"
                  :items="sources"
                  hide-details
                  clearable
                ></v-select>
              </v-flex>
            </v-layout>
            <v-layout row align-center class="mx-0 mt-3 mb-2">
              <v-btn
                small
                outline
                color="success"
                class="ml-0"
                :disabled="!staffRows.length"
                @click="selectAllStaff()"
              >
                <v-icon small left>mdi-checkbox-multiple-marked-outline</v-icon>
                Select all
              </v-btn>
              <v-btn
                small
                outline
                color="grey darken-1"
                :disabled="!staffSelected.length"
                @click="deselectAllStaff()"
              >
                <v-icon small left>mdi-checkbox-multiple-blank-outline</v-icon>
                Deselect all
              </v-btn>
              <span class="ml-2 grey--text text--darken-1">
                <strong>{{ staffSelected.length }}</strong>
                of {{ staffRows.length }} selected
              </span>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="staffSearch"
                prepend-inner-icon="mdi-magnify"
                placeholder="Filter"
                single-line
                hide-details
                clearable
                class="mt-0 pt-0"
                style="max-width: 260px"
              ></v-text-field>
            </v-layout>
            <v-data-table
              :headers="staffHeaders"
              :items="staffFilteredRows"
              :pagination.sync="staffPagination"
              :loading="staffLoading"
              hide-actions
              disable-initial-sort
              item-key="employeeID"
              v-model="staffSelected"
              class="elevation-1 copy-table compact"
            >
              <template v-slot:items="props">
                <tr
                  class="copy-row"
                  @click="props.selected = !props.selected"
                >
                  <td>
                    <v-checkbox
                      v-model="props.selected"
                      color="success"
                      hide-details
                      @click.native.stop
                    ></v-checkbox>
                  </td>
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.type }}</td>
                  <td>{{ props.item.students }}</td>
                  <td>{{ classLabel(props.item) }}</td>
                  <td @click.stop>
                    <v-text-field
                      v-model="props.item.relationInfo"
                      single-line
                      hide-details
                      class="mt-0 pt-0 staff-relation"
                      :error="props.selected && !props.item.relationInfo"
                    ></v-text-field>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <div class="text-xs-center pa-4 grey--text">
                  {{ staffEmptyText }}
                </div>
              </template>
            </v-data-table>
            <v-card-actions class="mx-0 px-0 pb-0 mt-3">
              <v-spacer></v-spacer>
              <v-btn color="default" dark @click="newRelationDialog = false"
                >Cancel</v-btn
              >
              <v-btn
                class="ml-3"
                color="success"
                :disabled="!staffSelected.length"
                @click="openStaffConfirm()"
                >Next ({{ staffSelected.length }})</v-btn
              >
            </v-card-actions>
          </template>

          <template v-else-if="staffConfirming">
            <div class="subheading mt-3 mb-2">
              Add <strong>{{ staffSelected.length }}</strong> relations to
              <strong>{{ newRelation.accountID }}</strong>
              <span v-if="newRelation.account.hname" class="grey--text">
                ({{ newRelation.account.hname }})
              </span>
              for <strong>{{ staffTargetRegID }}</strong>
            </div>
            <div
              v-if="staffComment || staffSource"
              class="caption grey--text text--darken-1 mb-2"
            >
              <span v-if="staffComment">Comment: {{ staffComment }}</span>
              <span v-if="staffComment && staffSource"> · </span>
              <span v-if="staffSource">Source: {{ staffSource }}</span>
            </div>
            <v-data-table
              :headers="staffConfirmHeaders"
              :items="staffSelected"
              :pagination.sync="staffConfirmPagination"
              hide-actions
              disable-initial-sort
              item-key="employeeID"
              class="elevation-1 copy-table compact"
            >
              <template v-slot:items="props">
                <td>{{ props.item.name }}</td>
                <td>{{ props.item.type }}</td>
                <td>{{ props.item.students }}</td>
                <td>{{ props.item.relationInfo }}</td>
                <td class="text-xs-right">
                  <v-btn
                    icon
                    small
                    class="ma-0 copy-remove"
                    title="Remove"
                    @click="removeStaffRow(props.item)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </td>
              </template>
            </v-data-table>
            <v-card-actions class="mx-0 px-0 pb-0 mt-3">
              <v-btn color="default" dark @click="staffConfirming = false"
                >Back</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn color="default" dark @click="newRelationDialog = false"
                >Cancel</v-btn
              >
              <v-btn
                class="ml-3"
                color="success"
                :loading="processing"
                :disabled="!staffSelected.length"
                @click="saveStaffRelations()"
                >Submit</v-btn
              >
            </v-card-actions>
          </template>

          <template v-else>
            <div class="subheading mt-3 mb-2">
              Added <strong>{{ staffResult.added }}</strong> to
              <strong>{{ staffTargetRegID }}</strong>
              <span v-if="staffResult.skipped" class="grey--text">
                · {{ staffResult.skipped }} already there
              </span>
            </div>
            <v-data-table
              :headers="staffResultHeaders"
              :items="staffResult.results"
              :pagination.sync="staffResultPagination"
              hide-actions
              disable-initial-sort
              item-key="employeeID"
              class="elevation-1 copy-table compact"
            >
              <template v-slot:items="props">
                <td>{{ props.item.name }}</td>
                <td>{{ props.item.relationInfo }}</td>
                <td>
                  <v-chip
                    small
                    class="ma-0"
                    :color="statusColor(props.item.status)"
                    >{{ props.item.status }}</v-chip
                  >
                </td>
              </template>
            </v-data-table>
            <v-card-actions class="mx-0 px-0 pb-0 mt-3">
              <v-btn color="default" dark @click="addMoreStaff()"
                >Add more</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn color="success" @click="newRelationDialog = false"
                >Done</v-btn
              >
            </v-card-actions>
          </template>
        </template>

        <template v-else>
        <v-autocomplete
          class="mb-3"
          hide-details
          label="Account"
          placeholder="Start typing..."
          hide-no-data
          ref="accountID"
          v-model="newRelation.accountID"
          :items="accounts"
          :search-input.sync="loadAccounts"
          :rules="[rules.required]"
          @change="onAccountSelected"
        ></v-autocomplete>
        <div v-if="newRelation.account.accountID" class="mb-3">
          Account: {{ newRelation.account.hname }}
        </div>
        <v-select
          class="mb-3"
          label="Term"
          :items="terms"
          v-model="regID"
          hide-details
          @change="onTermSelected"
        ></v-select>
        <v-autocomplete
          class="mb-3"
          hide-details
          label="Teacher"
          placeholder="Start typing..."
          hide-no-data
          ref="employeeID"
          v-model="newRelation.employeeID"
          :items="teachers"
          :search-input.sync="loadTeachers"
          @change="onTeacherSelected"
          :rules="[rules.required]"
        ></v-autocomplete>
        <div class="pb-2" v-if="newRelation.teacher.employeeID">
          Teacher: {{ newRelation.teacher.displayName }}
        </div>
        <v-text-field
          class="pt-1"
          v-model="newRelation.relationInfo"
          label="Relation"
          ref="relationInfo"
          :rules="[rules.required]"
        ></v-text-field>
        <v-text-field
          class="pt-1"
          v-model="newRelation.comment"
          label="Comment"
        ></v-text-field>
        <v-select
          class="pt-1"
          v-model="newRelation.source"
          label="Source"
          :items="sources"
          hide-details
          clearable
        ></v-select>
        <v-card-actions class="mx-0 px-2 pb-2 mt-2">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="newRelationDialog = false"
            >Cancel</v-btn
          >
          <v-btn :loading="processing" color="success" @click="saveRelation()">
            Save
          </v-btn>
        </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="copyRelationsDialog" max-width="1100">
      <v-card>
        <v-card-title class="headline pb-0">Copy Relations</v-card-title>
        <v-stepper v-model="copyStep" class="elevation-0">
          <v-stepper-header>
            <v-stepper-step :complete="copyStep > 1" step="1"
              >Terms</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="copyStep > 2" step="2"
              >Review</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step :complete="copyStep > 3" step="3"
              >Confirm</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step step="4">Results</v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <div class="mb-3">
                Pick the term to copy from and the term to copy to. Next you
                will see the list and can untick anything you don't want.
              </div>
              <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                  <v-select
                    label="From Term"
                    :items="terms"
                    v-model="copyFromRegID"
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <v-select
                    label="To Term"
                    :items="terms"
                    v-model="copyToRegID"
                  ></v-select>
                </v-flex>
              </v-layout>
              <div
                v-if="
                  copyFromRegID && copyToRegID && copyFromRegID === copyToRegID
                "
                class="error--text mb-2"
              >
                Pick two different terms.
              </div>
              <v-layout row class="mx-0">
                <v-spacer></v-spacer>
                <v-btn color="default" dark @click="copyRelationsDialog = false"
                  >Cancel</v-btn
                >
                <v-btn
                  class="ml-1"
                  color="success"
                  :loading="processing"
                  :disabled="!canCopyRelations"
                  @click="loadCopyPreview()"
                  >Next</v-btn
                >
              </v-layout>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-layout row align-center class="mx-0 mb-2">
                <v-btn
                  small
                  outline
                  color="success"
                  class="ml-0"
                  @click="selectShownCopyRows()"
                >
                  <v-icon small left
                    >mdi-checkbox-multiple-marked-outline</v-icon
                  >
                  Select all
                </v-btn>
                <v-btn
                  small
                  outline
                  color="grey darken-1"
                  @click="unselectShownCopyRows()"
                >
                  <v-icon small left
                    >mdi-checkbox-multiple-blank-outline</v-icon
                  >
                  Deselect all
                </v-btn>
                <span class="ml-2 grey--text text--darken-1">
                  <strong>{{ copySelectedRows.length }}</strong>
                  of {{ copyRows.length }} selected
                </span>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="copySearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Filter"
                  single-line
                  hide-details
                  clearable
                  class="mt-0 pt-0"
                  style="max-width: 280px"
                ></v-text-field>
              </v-layout>
              <v-data-table
                :headers="copyHeaders"
                :items="copyFilteredRows"
                :pagination.sync="copyPagination"
                hide-actions
                disable-initial-sort
                item-key="id"
                v-model="copySelected"
                class="elevation-1 copy-table"
              >
                <template v-slot:items="props">
                  <tr
                    class="copy-row"
                    @click="props.selected = !props.selected"
                  >
                    <td>
                      <v-checkbox
                        v-model="props.selected"
                        color="success"
                        hide-details
                        @click.native.stop
                      ></v-checkbox>
                    </td>
                    <td>{{ props.item.accountID }}</td>
                    <td>{{ props.item.name }}</td>
                    <td>{{ props.item.employeeID }}</td>
                    <td>{{ props.item.teacher }}</td>
                    <td>{{ props.item.relationInfo }}</td>
                    <td>{{ props.item.comment }}</td>
                    <td>{{ props.item.source }}</td>
                  </tr>
                </template>
                <template v-slot:no-data>
                  <div class="text-xs-center pa-4 grey--text">
                    {{
                      copyRows.length
                        ? "No relations match this filter"
                        : "Nothing new to copy to " + copyToRegID
                    }}
                  </div>
                </template>
              </v-data-table>
              <v-layout row class="mx-0 mt-3">
                <v-btn color="default" dark @click="copyStep = 1">Back</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="default" dark @click="copyRelationsDialog = false"
                  >Cancel</v-btn
                >
                <v-btn
                  class="ml-1"
                  color="success"
                  :disabled="!copySelectedRows.length"
                  @click="copyStep = 3"
                  >Next ({{ copySelectedRows.length }})</v-btn
                >
              </v-layout>
            </v-stepper-content>

            <v-stepper-content step="3">
              <div class="subheading mb-2">
                Copy
                <strong>{{ copySelectedRows.length }}</strong>
                relations from
                <strong>{{ copyFromRegID }}</strong> to
                <strong>{{ copyToRegID }}</strong>
              </div>
              <v-data-table
                :headers="copyConfirmHeaders"
                :items="copySelectedRows"
                :pagination.sync="copyConfirmPagination"
                hide-actions
                disable-initial-sort
                item-key="id"
                class="elevation-1 copy-table compact"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.accountID }}</td>
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.teacher }}</td>
                  <td>{{ props.item.relationInfo }}</td>
                  <td class="text-xs-right">
                    <v-btn
                      icon
                      small
                      class="ma-0 copy-remove"
                      title="Remove"
                      @click="removeCopyRow(props.item)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </template>
              </v-data-table>
              <v-layout row class="mx-0 mt-3">
                <v-btn color="default" dark @click="copyStep = 2">Back</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="default" dark @click="copyRelationsDialog = false"
                  >Cancel</v-btn
                >
                <v-btn
                  class="ml-1"
                  color="success"
                  :loading="processing"
                  :disabled="!copySelectedRows.length"
                  @click="copyRelations()"
                  >Submit</v-btn
                >
              </v-layout>
            </v-stepper-content>

            <v-stepper-content step="4">
              <div class="subheading mb-2">
                Copied
                <strong>{{ copyResult.copied }}</strong> to
                <strong>{{ copyToRegID }}</strong>
                <span v-if="copyResult.skipped" class="grey--text">
                  · {{ copyResult.skipped }} already there
                </span>
                <span v-if="copyResult.notFound" class="grey--text">
                  · {{ copyResult.notFound }} no longer found
                </span>
              </div>
              <v-data-table
                :headers="copyResultHeaders"
                :items="copyResult.results"
                :pagination.sync="copyResultPagination"
                hide-actions
                disable-initial-sort
                item-key="id"
                class="elevation-1 copy-table compact"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.accountID }}</td>
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.teacher }}</td>
                  <td>{{ props.item.relationInfo }}</td>
                  <td>
                    <v-chip
                      small
                      class="ma-0"
                      :color="statusColor(props.item.status)"
                      >{{ props.item.status }}</v-chip
                    >
                  </td>
                </template>
              </v-data-table>
              <v-layout row class="mx-0 mt-3">
                <v-spacer></v-spacer>
                <v-btn color="success" @click="copyRelationsDialog = false"
                  >Done</v-btn
                >
              </v-layout>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="deleteRelationsDialog" max-width="400">
      <v-card class="pa-2">
        <v-card-title class="text-warning headline">Are You Sure?</v-card-title>
        <v-card-text class="mb-3 py-0 title">
          This will delete the selected relations
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteRelationsDialog = false"
            >Close</v-btn
          >
          <v-btn
            :loading="processing"
            color="success"
            @click="deleteRelations()"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { formatCurrency } from "../../utils/helpers.js";

export default {
  data: () => ({
    title: "Tip Transactions",
    url: "",
    actions: [],
    selected: [],
    newRelationDialog: false,
    deleteRelationsDialog: false,
    relationMode: "single",
    tipRegID: "",
    staffRegID: "",
    staffTargetRegID: "",
    staffRows: [],
    staffSelected: [],
    staffSearch: "",
    staffLoading: false,
    staffLoadedKey: "",
    staffComment: "",
    staffSource: "",
    staffResult: null,
    staffConfirming: false,
    staffConfirmPagination: { rowsPerPage: -1 },
    staffConfirmHeaders: [
      { text: "Staff", value: "name" },
      { text: "Position", value: "type" },
      { text: "Students", value: "students" },
      { text: "Relation", value: "relationInfo" },
      { text: "", value: "remove", sortable: false, width: "40" },
    ],
    staffPagination: { rowsPerPage: -1 },
    staffResultPagination: { rowsPerPage: -1 },
    staffHeaders: [
      { text: "", value: "selected", sortable: false, width: "40" },
      { text: "Staff", value: "name" },
      { text: "Position", value: "type" },
      { text: "Students", value: "students" },
      { text: "Class", value: "class" },
      { text: "Relation", value: "relationInfo", sortable: false },
    ],
    staffResultHeaders: [
      { text: "Staff", value: "name" },
      { text: "Relation", value: "relationInfo" },
      { text: "Status", value: "status" },
    ],
    copyRelationsDialog: false,
    copyStep: 1,
    copyFromRegID: "",
    copyToRegID: "",
    copyRows: [],
    copySelected: [],
    copySearch: "",
    copyPagination: { rowsPerPage: -1 },
    copyConfirmPagination: { rowsPerPage: -1 },
    copyResultPagination: { rowsPerPage: -1 },
    copyResult: { copied: 0, skipped: 0, notFound: 0, results: [] },
    copyHeaders: [
      { text: "", value: "selected", sortable: false, width: "40" },
      { text: "Account ID", value: "accountID" },
      { text: "Name", value: "name" },
      { text: "Employee ID", value: "employeeID" },
      { text: "Teacher", value: "teacher" },
      { text: "Relation", value: "relationInfo" },
      { text: "Comment", value: "comment" },
      { text: "Source", value: "source" },
    ],
    copyConfirmHeaders: [
      { text: "Account ID", value: "accountID" },
      { text: "Name", value: "name" },
      { text: "Teacher", value: "teacher" },
      { text: "Relation", value: "relationInfo" },
      { text: "", value: "remove", sortable: false, width: "40" },
    ],
    copyResultHeaders: [
      { text: "Account ID", value: "accountID" },
      { text: "Name", value: "name" },
      { text: "Teacher", value: "teacher" },
      { text: "Relation", value: "relationInfo" },
      { text: "Status", value: "status" },
    ],
    clickedRow: {},
    regID: "",
    processing: false,
    terms: [],

    loadAccounts: null,
    loadTeachers: null,

    accounts: [],
    teachers: [],
    sources: ["YK"],

    newRelation: {
      relationInfo: "",
      comment: "",
      source: "",
      accountID: "",
      employeeID: "",
      account: "",
      teacher: "",
    },

    rules: {
      required: (value) => !!value || "Required.",
    },

    columns: [
      {
        label: "Employee ID",
        field: "employeeID",
        visible: true,
        link: "TeacherView",
        template: "link-cell-template",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Name",
        field: "name",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Name Hebrew",
        field: "nameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Account ID",
        field: "accountID",
        link: "Account View",
        format: "",
        template: "link-cell-template",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Relation Info",
        field: "relationInfo",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Teacher",
        field: "teacherH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Added On",
        field: "addedOn",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortOrder: "desc",
        sortIndex: 0,
      },
      {
        label: "Comment",
        field: "comment",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Source",
        field: "source",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Reg ID",
        field: "regID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  computed: {
    canCopyRelations() {
      return (
        !!this.copyFromRegID &&
        !!this.copyToRegID &&
        this.copyFromRegID !== this.copyToRegID
      );
    },
    copyFilteredRows() {
      const search = (this.copySearch || "").trim().toLowerCase();

      return this.copyRows.filter((row) => {
        if (!search) return true;

        return [
          row.accountID,
          row.name,
          row.employeeID,
          row.teacher,
          row.relationInfo,
          row.comment,
          row.source,
        ].some((value) =>
          String(value == null ? "" : value)
            .toLowerCase()
            .includes(search)
        );
      });
    },
    copySelectedRows() {
      return this.copySelected;
    },
    staffFilteredRows() {
      const search = (this.staffSearch || "").trim().toLowerCase();
      if (!search) return this.staffRows;

      return this.staffRows.filter((row) =>
        [
          row.employeeID,
          row.name,
          row.type,
          row.students,
          this.classLabel(row),
          row.relationInfo,
        ].some((value) =>
          String(value == null ? "" : value)
            .toLowerCase()
            .includes(search)
        )
      );
    },
    staffEmptyText() {
      if (this.staffLoading) return "Loading...";
      if (!this.newRelation.accountID) return "Pick an account";
      if (!this.staffRegID || !this.staffTargetRegID) return "Pick both terms";
      if (this.staffRows.length) return "No staff match this filter";
      return "No staff found for " + this.staffRegID;
    },
  },
  watch: {
    relationMode(value) {
      if (value !== "staff") return;

      this.setStaffTermDefaults();
      this.loadTermStaff();
    },
    async loadAccounts(value) {
      if (!value) return;

      const response = await this.$http.get(
        "api/teacherRelations/getAccountIDs?value=" + value
      );

      this.accounts = response;
    },
    async loadTeachers(value) {
      if (!value) return;

      const response = await this.$http.get(
        "api/teacherRelations/getEmployeeIDs?value=" +
          value +
          "&regID=" +
          this.regID
      );

      this.teachers = response;
    },
  },
  created() {
    this.loadTerms();
    this.loadRegID();
    this.loadTipRegID();
  },
  methods: {
    async loadTerms() {
      const response = await this.$http.get("api/allStudents/getRegTerms");

      this.terms = response;
    },
    async loadRegID() {
      this.regID = await this.$http.get("api/allStudents/getRegID");
    },
    async loadTipRegID() {
      const value = await this.$http.get("api/teacherRelations/getTipRegID");
      this.tipRegID = String(value || "").trim();
    },
    beforeSetupGrid() {
      this.actions = [
        {
          text: "New Relation",
          action: this.addNewRelation,
        },
        {
          text: "Copy Relations",
          action: this.openCopyRelationsDialog,
        },
        {
          text: "Delete Relations",
          action: this.openDeleteRelationDialog,
          showOnSelection: true,
        },
      ];

      this.url = "teacherRelations";
    },
    addNewRelation() {
      this.newRelation = {
        relationInfo: "",
        comment: "",
        source: "",
        accountID: "",
        employeeID: "",
        account: "",
        teacher: "",
      };

      this.relationMode = "single";
      this.staffRegID = "";
      this.staffTargetRegID = "";
      this.staffRows = [];
      this.staffSelected = [];
      this.staffSearch = "";
      this.staffLoadedKey = "";
      this.staffComment = "";
      this.staffSource = "";
      this.staffResult = null;
      this.staffConfirming = false;

      this.newRelationDialog = true;
    },
    previousTerm(regID) {
      const match = /^(.*?)(\d{4})$/.exec((regID || "").trim());
      if (!match) return "";

      return match[1] + (parseInt(match[2], 10) - 1);
    },
    setStaffTermDefaults() {
      if (!this.staffTargetRegID)
        this.staffTargetRegID = this.tipRegID || this.regID || "";

      if (!this.staffRegID)
        this.staffRegID = this.previousTerm(this.staffTargetRegID);
    },
    async onAccountSelected(value) {
      if (this.relationMode === "staff") this.loadTermStaff();

      const response = await this.$http.get(
        "api/accounts/getAccount?accountID=" + value
      );

      this.newRelation.account = response;
    },
    classLabel(row) {
      return [row.branch, row.class, row.section]
        .filter((value) => value && String(value).trim())
        .join(" ");
    },
    async loadTermStaff() {
      const accountID = this.newRelation.accountID;

      if (
        this.relationMode !== "staff" ||
        !accountID ||
        !this.staffRegID ||
        !this.staffTargetRegID
      ) {
        this.staffRows = [];
        this.staffSelected = [];
        this.staffLoadedKey = "";
        return;
      }

      const key = [accountID, this.staffRegID, this.staffTargetRegID].join(
        "|"
      );
      if (key === this.staffLoadedKey && !this.staffResult) return;

      this.staffLoadedKey = key;
      this.staffResult = null;
      this.staffConfirming = false;
      this.staffLoading = true;
      this.staffRows = [];
      this.staffSelected = [];

      try {
        const response = await this.$http.get(
          "api/teacherRelations/getTermStaff?accountID=" +
            encodeURIComponent(accountID) +
            "&regID=" +
            encodeURIComponent(this.staffRegID) +
            "&targetRegID=" +
            encodeURIComponent(this.staffTargetRegID)
        );

        if (key !== this.staffLoadedKey) return;

        this.staffRows = (response || []).map((row) => ({
          ...row,
          relationInfo: row.type || "",
        }));
        this.staffLoading = false;
      } catch (e) {
        if (key === this.staffLoadedKey) {
          this.staffLoading = false;
          this.staffLoadedKey = "";
        }
      }
    },
    selectAllStaff() {
      const ids = new Set(this.staffSelected.map((row) => row.employeeID));

      this.staffSelected = this.staffSelected.concat(
        this.staffFilteredRows.filter((row) => !ids.has(row.employeeID))
      );
    },
    deselectAllStaff() {
      const shown = new Set(
        this.staffFilteredRows.map((row) => row.employeeID)
      );

      this.staffSelected = this.staffSelected.filter(
        (row) => !shown.has(row.employeeID)
      );
    },
    openStaffConfirm() {
      if (!this.staffSelected.length) return;

      if (this.staffSelected.some((row) => !(row.relationInfo || "").trim())) {
        return this.$notify({
          group: "notification",
          text: "Every selected row needs a relation",
          type: "error",
        });
      }

      this.staffConfirming = true;
    },
    removeStaffRow(item) {
      this.staffSelected = this.staffSelected.filter(
        (row) => row.employeeID !== item.employeeID
      );

      if (!this.staffSelected.length) this.staffConfirming = false;
    },
    async saveStaffRelations() {
      if (!this.staffSelected.length || this.processing) return;

      try {
        this.processing = true;

        const response = await this.$http.post(
          "api/teacherRelations/addFromStaff",
          {
            accountID: this.newRelation.accountID,
            regID: this.staffTargetRegID,
            comment: this.staffComment,
            source: this.staffSource,
            addedBy: this.$store.state.authentication.user,
            items: this.staffSelected.map((row) => ({
              employeeID: row.employeeID,
              name: row.name,
              relationInfo: row.relationInfo,
            })),
          }
        );

        this.staffConfirming = false;
        this.staffResult = {
          added: response.added || 0,
          skipped: response.skipped || 0,
          results: response.results || [],
        };

        this.$refs.grid.reloadGrid();
        this.processing = false;
      } catch (e) {
        this.processing = false;
      }
    },
    addMoreStaff() {
      this.staffResult = null;
      this.staffLoadedKey = "";
      this.loadTermStaff();
    },
    async onTeacherSelected(value) {
      const response = await this.$http.get(
        `odata/kesherEmployees?branch=&grade=&regID=&$filter=employeeID eq ${value}`
      );

      this.newRelation.teacher = response.value[0];
    },
    onTermSelected(value) {
      this.newRelation.employeeID = "";
      this.newRelation.teacher = "";
    },
    openCopyRelationsDialog() {
      this.copyStep = 1;
      this.copyFromRegID = this.regID || "";
      this.copyToRegID = "";
      this.copyRows = [];
      this.copySelected = [];
      this.copySearch = "";
      this.copyResult = {
        copied: 0,
        skipped: 0,
        notFound: 0,
        results: [],
      };
      this.copyRelationsDialog = true;
    },
    async loadCopyPreview() {
      if (!this.canCopyRelations || this.processing) return;

      try {
        this.processing = true;

        const response = await this.$http.get(
          "api/teacherRelations/getCopyPreview?fromRegID=" +
            encodeURIComponent(this.copyFromRegID) +
            "&toRegID=" +
            encodeURIComponent(this.copyToRegID)
        );

        this.copyRows = (response || []).filter((row) => row.canCopy);
        this.copySelected = this.copyRows.slice();
        this.copySearch = "";
        this.copyStep = 2;
        this.processing = false;
      } catch (e) {
        this.processing = false;
      }
    },
    selectShownCopyRows() {
      const ids = new Set(this.copySelected.map((row) => row.id));
      const toAdd = this.copyFilteredRows.filter((row) => !ids.has(row.id));

      this.copySelected = this.copySelected.concat(toAdd);
    },
    unselectShownCopyRows() {
      const shown = new Set(this.copyFilteredRows.map((row) => row.id));

      this.copySelected = this.copySelected.filter((row) => !shown.has(row.id));
    },
    removeCopyRow(item) {
      this.copySelected = this.copySelected.filter((row) => row.id !== item.id);

      if (!this.copySelectedRows.length) this.copyStep = 2;
    },
    statusColor(status) {
      if (status === "New" || status === "Copied" || status === "Added")
        return "green lighten-4";
      if (status === "Already on term") return "amber lighten-4";
      return "grey lighten-3";
    },
    async copyRelations() {
      if (
        !this.canCopyRelations ||
        !this.copySelectedRows.length ||
        this.processing
      )
        return;

      try {
        this.processing = true;

        const response = await this.$http.post("api/teacherRelations/copy", {
          fromRegID: this.copyFromRegID,
          toRegID: this.copyToRegID,
          ids: this.copySelectedRows.map((row) => row.id),
          addedBy: this.$store.state.authentication.user,
        });

        this.copyResult = {
          copied: response.copied || 0,
          skipped: response.skipped || 0,
          notFound: response.notFound || 0,
          results: response.results || [],
        };
        this.copyStep = 4;

        this.$refs.grid.reloadGrid();
        this.processing = false;
      } catch (e) {
        this.processing = false;
      }
    },
    openDeleteRelationDialog() {
      this.selected = this.$refs.grid.getSelectedRowsData();
      this.deleteRelationsDialog = true;
    },
    async deleteRelations() {
      try {
        this.processing = true;

        await this.$http.post(
          "api/teacherRelations/delete",
          this.selected.map((s) => s.id)
        );

        this.processing = false;

        this.$refs.grid.reloadGrid();

        this.deleteRelationsDialog = false;
      } catch (e) {
        this.processing = false;
      }
    },
    async saveRelation() {
      if (
        !this.$refs.accountID.validate(true) ||
        !this.$refs.employeeID.validate(true) ||
        !this.$refs.relationInfo.validate(true)
      )
        return;

      try {
        this.processing = true;

        await this.$http.post("api/teacherRelations/saveTeacherRealation", {
          employeeID: this.newRelation.employeeID,
          accountID: this.newRelation.accountID,
          regID: this.regID,
          relationInfo: this.newRelation.relationInfo,
          comment: this.newRelation.comment,
          source: this.newRelation.source,
          addedBy: this.$store.state.authentication.user,
        });

        this.processing = false;

        this.$refs.grid.reloadGrid();

        this.newRelationDialog = false;
      } catch (e) {
        this.processing = false;
      }
    },
  },
};
</script>
<style>
.copy-table .v-table__overflow {
  max-height: 50vh;
  overflow-y: auto;
}
.copy-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}
.copy-table .copy-row {
  cursor: pointer;
}
.copy-table td:first-child .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
.copy-table.compact table.v-table thead tr {
  height: 34px;
}
.copy-table.compact table.v-table tbody td,
.copy-table.compact table.v-table thead th {
  height: 30px;
  padding: 0 12px;
  font-size: 13px;
}
.relation-mode {
  display: flex;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
}
.relation-mode__option {
  flex: 1;
  padding: 8px 12px;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #616161;
  background: #fff;
  transition: background 0.15s, color 0.15s;
  user-select: none;
}
.relation-mode__option + .relation-mode__option {
  border-left: 1px solid #c8e6c9;
}
.relation-mode__option .v-icon {
  color: inherit;
  margin-right: 4px;
}
.relation-mode__option:hover {
  background: #f1f8e9;
}
.relation-mode__option.active {
  background: #4caf50;
  color: #fff;
}
.copy-table .staff-relation input {
  font-size: 13px;
  padding: 2px 0;
}
.copy-table.compact .copy-remove {
  width: 24px;
  height: 24px;
}
</style>
